/**
 * GenQuotes blog — static content store.
 *
 * Add a new post by appending an object to `BLOG_POSTS` below.
 * Field shape:
 *   slug            — URL segment (kebab-case, unique)
 *   title           — Full post title (rendered as <h1>)
 *   author          — Display byline (currently "GenQuotes Team")
 *   publishedAt     — ISO date string (YYYY-MM-DD)
 *   readingTime     — Display string (e.g. "5 min read")
 *   excerpt         — First 2-3 sentences; shown on the index card
 *                     AND used as the meta description (trimmed to 150 chars)
 *   ogImage         — Optional Open Graph share image URL
 *   content         — Array of blocks: { type: 'p' | 'h2', text: string }
 */

export const BLOG_POSTS = [
  {
    slug: "when-to-stop-doing-everything-yourself",
    title: "When to Stop Doing Everything Yourself and What to Hand Off First",
    author: "GenQuotes Team",
    publishedAt: "2026-08-31",
    readingTime: "5 min read",
    excerpt:
      "Most small business owners know they're doing too much. They don't need convincing. This is about how to figure out what goes first.",
    ogImage: "https://genquotes.com/genquotes-og.png",
    content: [
      { type: "p", text: "Most small business owners know they're doing too much." },
      {
        type: "p",
        text: "They don't need convincing. They can feel it in the 9pm emails, the missed calls during jobs, the weekends that aren't really weekends. The problem isn't awareness. The problem is knowing where to actually start.",
      },
      {
        type: "p",
        text: "So rather than making the case for handing things off, you already know you should, this is about how to figure out what goes first.",
      },

      { type: "h2", text: "The delegation test" },
      {
        type: "p",
        text: "Before you hand anything off, you need to know whether it's actually worth handing off. Not everything is. Some tasks look time-consuming but require judgment that genuinely needs to be yours. Others look simple but are so tangled up in your personal relationships with customers that automating them would actually make things worse.",
      },
      { type: "p", text: "A useful filter is three questions:" },
      {
        type: "p",
        text: "Does this task follow the same pattern every time? If the answer is yes, same questions, same process, same outcome, it's a strong candidate for automation or delegation. If every instance requires fresh thinking, it probably needs to stay with you for now.",
      },
      {
        type: "p",
        text: "Does it require a relationship? Some customer interactions are transactional. Others are relational. A booking confirmation is transactional. It needs to be accurate and timely, but it doesn't need to come from you personally. A conversation with a long-term customer who's had a problem is relational. That one needs you.",
      },
      {
        type: "p",
        text: "What's the cost of getting it wrong? Low-stakes tasks with predictable outcomes are safe to hand off early. High-stakes tasks, anything that could damage a customer relationship or create a liability, stay with you until you have a system you genuinely trust.",
      },
      {
        type: "p",
        text: "Run your weekly tasks through those three questions. The ones that pass all three are where you start.",
      },

      { type: "h2", text: "The order matters" },
      {
        type: "p",
        text: "Most business owners, when they decide to start automating or delegating, pick the thing that's annoying them most in that moment. That's understandable but it's not always the right call.",
      },
      {
        type: "p",
        text: "A better approach is to prioritise by two factors: how much time it's costing you, and how straightforward it is to hand off.",
      },
      {
        type: "p",
        text: "The tasks that score high on both are your first targets. For most service businesses that's quoting, booking confirmations, appointment reminders, and responses to common customer questions.",
      },
      {
        type: "p",
        text: "Quoting sits at the top of the list for a reason. It's time-consuming, it happens constantly, it follows a predictable pattern, and the cost of automating it is low. An online quoting tool handles the whole process. The customer answers a few questions, gets a price instantly, and accepts or moves on. You get a notification. No phone call, no back and forth, no interruption mid-job.",
      },
      {
        type: "p",
        text: "The time saving is real but the bigger benefit is less obvious. You stop losing jobs to faster competitors. A customer who gets an instant price at 9pm on a Sunday is far more likely to book than one who has to wait until Monday morning for a callback.",
      },

      { type: "h2", text: "What AI is actually good at right now" },
      {
        type: "p",
        text: "For most service businesses, AI is genuinely useful for a specific category of task: things that require thinking but follow a familiar structure.",
      },
      {
        type: "p",
        text: "Writing is the clearest example. Not all writing. AI won't replace the message you send a long-term customer after a difficult job. But the routine writing that fills your week: quote follow-ups, booking confirmations, responses to common questions, job descriptions when you need to hire, social media posts, Google review responses.",
      },
      {
        type: "p",
        text: "These tasks require enough judgment that you can't just fill in a template, but they're familiar enough that the thinking is almost automatic. AI tools can do the drafting. You do the editing. Something that used to take thirty minutes takes five.",
      },
      {
        type: "p",
        text: "The other area worth paying attention to is after-hours enquiries. A significant chunk of customer enquiries, particularly for trade and service businesses, come in outside business hours. Most of those enquiries get a response the next morning, if they get one at all. A chatbot or automated response tool can handle these in real time: answer common questions, provide a price guide, capture contact details, and flag anything that needs a human follow-up. The leads that used to go cold overnight start converting.",
      },

      { type: "h2", text: "Building the habit" },
      {
        type: "p",
        text: "The mistake most business owners make when they start delegating is trying to do too much at once. They research five tools, set up three systems, and get overwhelmed before any of it is actually working. A month later everything is back to how it was.",
      },
      {
        type: "p",
        text: "A more reliable approach is to treat each handoff as a project with a defined endpoint.",
      },
      {
        type: "p",
        text: "Pick one task. Identify the tool or system or person that can take it on. Set it up properly, don't rush it. Run it alongside your existing process for two weeks to make sure it's working correctly. Then let go of it completely.",
      },
      { type: "p", text: "Only then move to the next one." },
      {
        type: "p",
        text: "It takes longer. But six months from now you'll have five or six things genuinely off your plate, running reliably, without the chaos that comes from trying to change everything at once.",
      },
      {
        type: "p",
        text: "The business owners who seem to have the most breathing room aren't the ones who found a magic solution. They're the ones who made one small, permanent change at a time and kept going.",
      },
    ],
  },
  {
    slug: "getting-your-time-back",
    title: "Getting Your Time Back: What Actually Works for Small Service Businesses",
    author: "GenQuotes Team",
    publishedAt: "2026-08-23",
    readingTime: "5 min read",
    excerpt:
      "We have a mate who runs a small business. He's good at what he does — genuinely one of the best. His customers love him, his reputation is solid, and his phone never stops ringing.",
    ogImage: "https://genquotes.com/genquotes-og.png",
    content: [
      { type: "p", text: "We have a mate who runs a small business." },
      {
        type: "p",
        text: "He's good at what he does — genuinely one of the best. His customers love him, his reputation is solid, and his phone never stops ringing. From the outside it looks like success.",
      },
      { type: "p", text: "But we watched him slowly disappear." },
      {
        type: "p",
        text: "Missed birthdays. Late to his kids' events — when he made it at all. His wife covering for him at family dinners while he stood in the driveway finishing a call. Seven days a week, early mornings, late nights. Not because he was lazy or disorganised. Because the business had grown faster than his ability to manage it, and every system he was running — quoting, scheduling, follow-ups, invoicing, customer communication — was still him. All of it. Manually. Every single day.",
      },
      {
        type: "p",
        text: "He doesn't know this, but he's the reason we started thinking seriously about this problem.",
      },
      {
        type: "p",
        text: "Because here's the thing — his situation isn't unusual. Talk to almost any small service business owner and you'll hear a version of the same story. The business is working. The customers are happy. But the owner is drowning.",
      },
      {
        type: "p",
        text: "Growth is supposed to feel like winning. For a lot of small business owners, it just feels like more work.",
      },
      { type: "p", text: "So what actually helps?" },
      {
        type: "p",
        text: "The businesses that seem to have cracked it aren't necessarily working harder than everyone else. They've just stopped doing manually what could be done automatically.",
      },
      { type: "p", text: "That's it. That's the whole insight." },
      {
        type: "p",
        text: "Not a complicated management philosophy. Not a business degree. Just a clear-eyed look at where their time is actually going — and a willingness to hand some of those tasks over to a system, a tool, or someone else.",
      },
      { type: "p", text: "Here's where we see the biggest difference." },

      { type: "h2", text: "Quoting and responding to enquiries" },
      {
        type: "p",
        text: "For most service businesses, quoting is the hidden time thief. It looks like a quick task — answer a call, ask a few questions, give a price — but across a week it adds up to hours. Hours on the phone during jobs. Hours returning missed calls. Hours going back and forth trying to nail down exactly what the customer needs.",
      },
      {
        type: "p",
        text: "And here's the part that stings: a lot of those calls don't convert. The customer was shopping around. You spent twenty minutes quoting a job that went to someone else.",
      },
      {
        type: "p",
        text: "An automated quoting tool on your website changes the equation. The customer gets a price instantly — no phone call needed. The ones who are serious accept it on the spot. The ones who aren't move on without costing you anything. Either way, you didn't have to stop what you were doing.",
      },
      {
        type: "p",
        text: "The best part? It works around the clock. Enquiries that come in at 9pm on a Sunday get a response immediately. By the time you start Monday morning, some of them are already booked.",
      },

      { type: "h2", text: "Customer communication" },
      {
        type: "p",
        text: "How much of your week is spent writing the same messages over and over?",
      },
      {
        type: "p",
        text: "Booking confirmations. Appointment reminders. Follow-ups after a job. Requests for Google reviews. Responses to the same questions you get asked constantly.",
      },
      {
        type: "p",
        text: "Each one takes a few minutes. Across a week that's hours. Across a year it's weeks of your life spent on messages that follow a completely predictable pattern.",
      },
      {
        type: "p",
        text: "Email automation and templated messaging can handle most of this. You write the message once and it goes out automatically at the right time. The customer experience actually improves because the communication is timely and consistent. And you stop having to remember to send things.",
      },

      { type: "h2", text: "Where AI fits in" },
      {
        type: "p",
        text: "AI has become genuinely useful for small businesses — not in the dramatic, everything-changes way it gets talked about in the media, but in a quieter, more practical way.",
      },
      {
        type: "p",
        text: "The most useful application for most service business owners right now is writing. Drafting emails. Writing job descriptions. Putting together a pricing guide or a staff onboarding document. Creating social media posts.",
      },
      {
        type: "p",
        text: "Tools like ChatGPT won't replace your voice or your judgment — but they're good at getting you past the blank page. You give them a rough idea, they produce a starting point, you edit it into shape. Something that used to take an hour takes ten minutes.",
      },
      {
        type: "p",
        text: "For business owners who didn't get into their trade because they love writing — which is most of them — this is genuinely useful.",
      },
      {
        type: "p",
        text: "AI is also increasingly helpful for answering customer questions. A simple chatbot on your website can handle the questions you get asked most often, around the clock. Not every business needs one. But if a significant chunk of your enquiries are people asking the same basic things — it's worth looking at.",
      },
      {
        type: "p",
        text: "None of this is about replacing the human parts of running a business. The relationships. The expertise. The judgment calls that come from years of experience. Those things can't be automated and shouldn't be.",
      },
      {
        type: "p",
        text: "What can be automated is the repetition. The admin. The back and forth. The tasks that follow the same pattern every single time.",
      },
      {
        type: "p",
        text: "The service businesses that seem to have the most breathing room aren't necessarily bigger or better resourced. They've just got better at identifying where their time is going — and ruthless about protecting it.",
      },

      { type: "h2", text: "Where to start" },
      { type: "p", text: "Pick one thing. Just one." },
      {
        type: "p",
        text: "What's the task that interrupts you most during the week? What do you do repeatedly that follows exactly the same pattern every time? What keeps getting pushed to tomorrow because you never quite have the time?",
      },
      {
        type: "p",
        text: "Start there. Find a tool, a template, or a system that can take it off your plate. Get comfortable with that before adding anything else.",
      },
      {
        type: "p",
        text: "Small improvements compound. An hour saved here, thirty minutes there — across a year it adds up to something significant. Not just in productivity. In the kind of business that's actually worth running.",
      },
    ],
  },
];

export function findPostBySlug(slug) {
  return BLOG_POSTS.find((p) => p.slug === slug) || null;
}

export function fmtPostDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}
